import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../../utils/api'
import { toast } from 'react-toastify';
import { useAccountContext } from '../AdminAccountPage';
import { Property, BrandProfile } from '../types';
import { useSortableData, SortDirection } from '../../components/SortableTable';

// ========================================
// Styled Components - Matching Semantic UI
// ========================================

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #2185d0;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #1678c2;
  }
  
  &::after {
    content: '▼';
    font-size: 10px;
  }
`;

const DropdownMenu = styled.div<{ $open?: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 100;
  display: ${props => props.$open ? 'block' : 'none'};
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 10px 16px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  font-size: 14px;
  
  &:hover {
    background: #f9fafb;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 24px;
`;

const FilterGroup = styled.div`
  flex: 1;
  max-width: 400px;
  position: relative;
`;

const SearchableSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableSelectInput = styled.div<{ $hasValue?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 9px 12px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: text;
  
  &:focus-within {
    border-color: #85b7d9;
  }
  
  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    background: transparent;
    color: ${({ $hasValue }) => $hasValue ? 'rgba(0, 0, 0, 0.87)' : 'inherit'};
    
    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: #e0e0e0;
  border-radius: 50%;
  cursor: pointer;
  color: #666;
  font-size: 12px;
  margin-right: 4px;
  
  &:hover {
    background: #ccc;
    color: #333;
  }
`;

const SearchableSelectDropdown = styled.div<{ $open?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.$open ? 'block' : 'none'};
  margin-top: 2px;
`;

const SearchableSelectItem = styled.div<{ $isHighlighted?: boolean }>`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  background: ${({ $isHighlighted }) => $isHighlighted ? '#f0f0f0' : 'white'};
  
  &:hover {
    background: #f0f0f0;
  }
`;

const NoResults = styled.div`
  padding: 10px 14px;
  color: #9ca3af;
  font-size: 14px;
  font-style: italic;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  ${({ $primary }) => $primary ? `
    background: #2185d0;
    color: white;
    &:hover { background: #1678c2; }
  ` : `
    background: #e0e1e2;
    color: rgba(0, 0, 0, 0.6);
    &:hover { background: #cacbcd; }
  `}
  
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid rgba(34, 36, 38, 0.15);
  
  td {
    text-align: left;
    padding: 11px 12px;
    border-top: 1px solid rgba(34, 36, 38, 0.1);
  }
  
  tr:hover td {
    background: #f9fafb;
  }
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 11px 12px;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : 'rgba(0, 0, 0, 0.87)')};
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      color: #00a6c2;
      background: #f0f0f0;
    `}
  }
`;

const PropertySortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  display: inline-block;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
    font-size: 12px;
  }
`;

const TableLink = styled(Link)`
  color: #4183c4;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ActionIcon = styled.button<{ $disabled?: boolean }>`
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #374151;
  width: 28px;
  height: 28px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  margin-right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  
  &:hover {
    background: ${props => props.$disabled ? 'white' : '#f3f4f6'};
  }
`;

const ExternalLinkIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #4183c4;
  
  &:hover {
    color: #1678c2;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmptyMessage = styled.p`
  padding: 20px;
  color: #666;
  text-align: center;
`;

const LoadingMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: #666;
`;

const PublicationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// ========================================
// Property Type Constants
// ========================================

const PROPERTY_TYPES = {
  docalytics: 'Docalytics',
  docalytics_custom_domain: 'Docalytics Custom Domain',
  performance: 'Performance',
  default: 'Default',
};

// ========================================
// Component
// ========================================

function AccountPropertiesTab() {
  const { account } = useAccountContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [properties, setProperties] = useState<Property[]>([]);
  const [publications, setPublications] = useState<BrandProfile[]>([]);
  const [selectedPublication, setSelectedPublication] = useState(
    searchParams.get('brand_profile') || ''
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Searchable filter state
  const [filterSearchQuery, setFilterSearchQuery] = useState('');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const filterContainerRef = useRef<HTMLDivElement>(null);

  // Get selected publication name for display
  const selectedPublicationName = useMemo(() => {
    if (!selectedPublication) return '';
    const pub = publications.find(p => String(p.id) === selectedPublication);
    return pub?.name || '';
  }, [selectedPublication, publications]);

  // Filter publications based on search query
  const filteredPublications = useMemo(() => {
    if (!filterSearchQuery.trim()) return publications;
    const query = filterSearchQuery.toLowerCase();
    return publications.filter(pub => 
      pub.name.toLowerCase().includes(query)
    );
  }, [publications, filterSearchQuery]);

  // Click outside handler for filter dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterContainerRef.current && !filterContainerRef.current.contains(event.target as Node)) {
        setFilterDropdownOpen(false);
        // Reset search query to selected value if no selection made
        if (selectedPublication) {
          setFilterSearchQuery(selectedPublicationName);
        } else {
          setFilterSearchQuery('');
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedPublication, selectedPublicationName]);

  // Initialize filter search query from URL param
  useEffect(() => {
    if (selectedPublicationName) {
      setFilterSearchQuery(selectedPublicationName);
    }
  }, [selectedPublicationName]);

  useEffect(() => {
    if (account) {
      // Use publications from account context
      setPublications(account.brandProfiles || []);
      fetchProperties();
    }
  }, [account]);

  const fetchProperties = async (publicationId?: string) => {
    if (!account) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (publicationId) {
        params.append('brand_profile', publicationId);
      }
      
      const response = await api.get(
        `/admin2/accounts/${account.id}/properties.json?${params.toString()}`
      );
      
      const data = response.data;
      setProperties((data.properties || data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        siteType: p.site_type || p.siteType,
        siteId: p.site_id || p.siteId,
        siteUrl: p.site_url || p.siteUrl,
        firstTracked: p.first_tracked || p.firstTracked,
        brandProfiles: p.brand_profiles || p.brandProfiles || [],
      })));
    } catch (error) {
      console.error('Failed to load properties:', error);
      toast.error('Failed to load properties');
      setProperties([]);
    }
    setLoading(false);
  };

  const handleFilter = () => {
    if (selectedPublication) {
      setSearchParams({ brand_profile: selectedPublication });
    } else {
      setSearchParams({});
    }
    fetchProperties(selectedPublication);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA');
  };

  const getPropertyTypeLabel = (type: string) => {
    return PROPERTY_TYPES[type as keyof typeof PROPERTY_TYPES] || type;
  };

  // Filter dropdown handlers
  const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterSearchQuery(e.target.value);
    setFilterDropdownOpen(true);
    setHighlightedIndex(-1);
  };

  const handleFilterInputFocus = () => {
    setFilterDropdownOpen(true);
  };

  const handleSelectPublication = (pub: BrandProfile) => {
    setSelectedPublication(String(pub.id));
    setFilterSearchQuery(pub.name);
    setFilterDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClearFilter = () => {
    setSelectedPublication('');
    setFilterSearchQuery('');
    setFilterDropdownOpen(false);
    setHighlightedIndex(-1);
    filterInputRef.current?.focus();
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filterDropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setFilterDropdownOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredPublications.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredPublications.length) {
          handleSelectPublication(filteredPublications[highlightedIndex]);
        }
        break;
      case 'Escape':
        setFilterDropdownOpen(false);
        if (selectedPublication) {
          setFilterSearchQuery(selectedPublicationName);
        } else {
          setFilterSearchQuery('');
        }
        break;
    }
  };

  // Flatten properties for sorting - extract first publication name for sorting
  const flattenedProperties = useMemo(() =>
    properties.map(p => ({
      ...p,
      publicationName: p.brandProfiles?.[0]?.name || '',
      typeLabel: getPropertyTypeLabel(p.siteType),
    })),
    [properties]
  );

  const { sortedData, requestSort, getSortDirection } = useSortableData(flattenedProperties, { key: 'name', direction: 'asc' });

  if (!account) return null;

  return (
    <div>
      <HeaderRow>
        <SectionTitle>Properties</SectionTitle>
        
        <DropdownContainer>
          <DropdownButton 
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
          >
            Create new property
          </DropdownButton>
          <DropdownMenu $open={dropdownOpen}>
            {/* Docalytics sunset: creation options hidden
            <DropdownItem to={`/admin2/accounts/${account.id}/properties/new?site_type=docalytics`}>
              Docalytics
            </DropdownItem>
            <DropdownItem to={`/admin2/accounts/${account.id}/properties/new?site_type=docalytics_custom_domain`}>
              Docalytics Custom Domain
            </DropdownItem>
            */}
            <DropdownItem to={`/admin2/accounts/${account.id}/properties/new?site_type=performance`}>
              Performance
            </DropdownItem>
          </DropdownMenu>
        </DropdownContainer>
      </HeaderRow>
      
      <FilterRow>
        <FilterGroup ref={filterContainerRef}>
          <SearchableSelectContainer>
            <SearchableSelectInput $hasValue={!!selectedPublication}>
              {selectedPublication && (
                <ClearButton
                  type="button"
                  onClick={handleClearFilter}
                  title="Clear filter"
                >
                  ×
                </ClearButton>
              )}
              <input
                ref={filterInputRef}
                type="text"
                value={filterSearchQuery}
                onChange={handleFilterInputChange}
                onFocus={handleFilterInputFocus}
                onKeyDown={handleFilterKeyDown}
                placeholder="Filter by instance"
              />
            </SearchableSelectInput>
            <SearchableSelectDropdown $open={filterDropdownOpen}>
              {filteredPublications.length === 0 ? (
                <NoResults>No matching publications</NoResults>
              ) : (
                filteredPublications.map((pub, index) => (
                  <SearchableSelectItem
                    key={pub.id}
                    $isHighlighted={index === highlightedIndex}
                    onClick={() => handleSelectPublication(pub)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {pub.name}
                  </SearchableSelectItem>
                ))
              )}
            </SearchableSelectDropdown>
          </SearchableSelectContainer>
        </FilterGroup>
        <Button type="button" $primary onClick={handleFilter}>
          Go
        </Button>
      </FilterRow>
      
      {loading ? (
        <LoadingMessage>Loading properties...</LoadingMessage>
      ) : properties.length === 0 ? (
        <EmptyMessage>
          No properties found.
        </EmptyMessage>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th 
                $sortable 
                $sorted={getSortDirection('publicationName') !== null}
                onClick={() => requestSort('publicationName')}
              >
                Publications <PropertySortIndicator $direction={getSortDirection('publicationName')} />
              </Th>
              <Th 
                $sortable 
                $sorted={getSortDirection('name') !== null}
                onClick={() => requestSort('name')}
              >
                Name <PropertySortIndicator $direction={getSortDirection('name')} />
              </Th>
              <Th 
                $sortable 
                $sorted={getSortDirection('typeLabel') !== null}
                onClick={() => requestSort('typeLabel')}
              >
                Type <PropertySortIndicator $direction={getSortDirection('typeLabel')} />
              </Th>
              <Th 
                $sortable 
                $sorted={getSortDirection('siteId') !== null}
                onClick={() => requestSort('siteId')}
              >
                Site ID <PropertySortIndicator $direction={getSortDirection('siteId')} />
              </Th>
              <Th 
                $sortable 
                $sorted={getSortDirection('siteUrl') !== null}
                onClick={() => requestSort('siteUrl')}
              >
                Site URL <PropertySortIndicator $direction={getSortDirection('siteUrl')} />
              </Th>
              <Th 
                $sortable 
                $sorted={getSortDirection('firstTracked') !== null}
                onClick={() => requestSort('firstTracked')}
              >
                First tracked <PropertySortIndicator $direction={getSortDirection('firstTracked')} />
              </Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map(property => (
              <tr key={property.id}>
                <td>
                  <PublicationsList>
                    {property.brandProfiles?.map(bp => (
                      <TableLink 
                        key={bp.id} 
                        to={`/admin2/publications/${bp.id}/edit`}
                      >
                        {bp.name}
                      </TableLink>
                    )) || '-'}
                  </PublicationsList>
                </td>
                <td>{property.name}</td>
                <td>{property.typeLabel}</td>
                <td>
                  <code style={{ fontSize: 12 }}>{property.siteId}</code>
                </td>
                <td>
                  {property.siteUrl && (
                    <ExternalLinkIcon
                      href={property.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open in new tab"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    </ExternalLinkIcon>
                  )}
                </td>
                <td>{formatDate(property.firstTracked) || '-'}</td>
                <td>
                  <ActionIcon
                    type="button"
                    title={property.firstTracked ? 'View property details' : 'No tracking data available'}
                    $disabled={!property.firstTracked}
                    onClick={() => {
                      if (property.firstTracked) {
                        navigate(`/admin2/accounts/${account.id}/properties/${property.id}`);
                      }
                    }}
                  >
                    ℹ️
                  </ActionIcon>
                  {(property.siteType === 'performance' || property.siteType === 'docalytics' || property.siteType === 'docalytics_custom_domain') && (
                    <ActionIcon
                      type="button"
                      title="Edit property"
                      onClick={() => navigate(`/admin2/accounts/${account.id}/properties/${property.id}/edit`)}
                    >
                      ✏️
                    </ActionIcon>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default AccountPropertiesTab;
